import { T } from '@tolgee/react';
import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import theme from '../../../assets/theme';
import { useAiFeedbackOverlayVisible } from '../../../providers/AiFeedbackOverlayVisibleProvider';
import { zIndexes } from '../../../utils/zIndexes';
import AnticipationLoader from '../../_atoms/AnticipationLoader/AnticipationLoader';

const PersonIcon = () => (
  <svg viewBox="0 0 36 36" fill="currentColor" aria-hidden="true">
    <path d="m27.5 34h-19c-1.3 0-2.6-.7-3.3-1.8-.8-1.1-.9-2.5-.4-3.8 2-5.1 7.3-8.5 13.2-8.5s11.2 3.4 13.2 8.5c.5 1.2.4 2.7-.4 3.8-.7 1.1-2 1.8-3.3 1.8z" />
    <path d="m18 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z" />
  </svg>
);

type AiFeedbackScoreboardProps = {
  scores: number[];
  maxScore: number; // -1 = unlimited (derive from data)
  totalStudents: number;
  // True while the AI is generating scores. When the backend ack lands and we
  // have all scores, the parent flips this to false. The component reads it +
  // scores.length to drive the V52/V53 status strip ("scoring → all scored").
  isScoring: boolean;
  onSeeAllResponses?: () => void;
};

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="60%"
    height="60%"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

const ANIMATION_DURATION_MS = 750;
const EMPTY_SLIVER_PCT = 2.5; // V37 ghost bars for zero-count rows

/**
 * Dense distribution: one bucket per integer in `[0, max]`, including empty
 * ones. Used only when `maxScore` is set explicitly by the designer (1–25
 * per the dropdown), so the array is small and bounded.
 */
function buildDistribution(rawScores: number[], max: number) {
  const dist = Array.from({ length: max + 1 }, (_, score) => ({ score, count: 0 }));
  rawScores.forEach(s => {
    const clamped = Math.max(0, Math.min(max, Math.round(s)));
    dist[clamped].count += 1;
  });
  return dist;
}

/**
 * Sparse distribution: only buckets that actually received a score. Used in
 * tally mode (`maxScore === -1`) where the upper bound is data-driven — a
 * rogue AI emitting a score of 100 000 would otherwise blow up the dense
 * allocation, even though tally rendering only ever shows non-empty buckets.
 */
function buildSparseDistribution(rawScores: number[]) {
  const counts = new Map<number, number>();
  rawScores.forEach(s => {
    const rounded = Math.max(0, Math.round(s));
    counts.set(rounded, (counts.get(rounded) ?? 0) + 1);
  });
  return Array.from(counts.entries(), ([score, count]) => ({ score, count }));
}

/**
 * Renders one score bucket. Owns its own animated-width state so that the
 * `--bar-w` CSS variable on `Row` and the explicit `width` on `BarFill` are
 * driven by the same source — keeping the bar fill and the count label
 * (positioned via `left: calc(var(--bar-w) + offset)` in non-tally mode) in
 * lockstep instead of letting the count jump to its final position while the
 * bar is still growing.
 */
const ScoreRow = ({
  bucket,
  index,
  isTally,
  started,
  isFull,
  isZero,
  targetPct,
}: {
  bucket: { score: number; count: number };
  index: number;
  isTally: boolean;
  started: boolean;
  isFull: boolean;
  isZero: boolean;
  targetPct: number;
}) => {
  const [animatedW, setAnimatedW] = useState(0);

  useEffect(() => {
    if (!started) {
      setAnimatedW(0);
      return;
    }
    // Defer one frame so the browser commits the 0% width before transitioning.
    const id = requestAnimationFrame(() => setAnimatedW(targetPct));
    return () => cancelAnimationFrame(id);
  }, [started, targetPct]);

  return (
    <Row
      $isFull={isFull}
      $isEmpty={isZero}
      $isTally={isTally}
      style={{
        animationDelay: `${index * 35}ms`,
        ['--bar-w' as never]: `${animatedW}%`,
      }}
    >
      <ScoreLabel $isFull={isFull}>
        <ScoreNum>{bucket.score}</ScoreNum>
        <ScoreUnit>
          <T keyName="Scoreboard.points" defaultValue="pts" />
        </ScoreUnit>
      </ScoreLabel>
      <BarTrack $isTally={isTally}>
        <BarFill
          style={{ width: `${animatedW}%` }}
          $isTally={isTally}
          $isEmpty={isZero}
        />
        <BarCount $isEmpty={isZero} $isTally={isTally}>
          <BarCountNum>{bucket.count}</BarCountNum>
          <BarCountIcon>
            <PersonIcon />
          </BarCountIcon>
        </BarCount>
      </BarTrack>
    </Row>
  );
};

const AiFeedbackScoreboard = ({
  scores,
  maxScore,
  totalStudents,
  isScoring,
  onSeeAllResponses,
}: AiFeedbackScoreboardProps) => {
  const overlayVisible = useAiFeedbackOverlayVisible();
  const started = !overlayVisible;

  // Progress = number of scored responses vs. total. With the current one-shot
  // ack architecture, this jumps from 0 → N when the backend resolves. If we
  // ever get a per-response progress event, this state graph stays the same.
  const received = scores.length;
  const safeTotal = Math.max(0, totalStudents);
  const isDone = !isScoring && safeTotal > 0 && received >= safeTotal;
  const progress =
    safeTotal === 0 ? (isDone ? 100 : 0) : Math.min(100, (received / safeTotal) * 100);

  const effectiveMax = useMemo(() => {
    if (maxScore === -1) {
      return Math.max(1, ...scores.map(s => Math.max(0, Math.round(s))));
    }
    return Math.max(0, Math.round(maxScore));
  }, [maxScore, scores]);

  const isTally = maxScore === -1;

  // V51 tally mode (unlimited max) renders only the buckets that received a
  // score, so we use a sparse Map-based distribution — both for correctness
  // and to avoid allocating a dense `effectiveMax + 1` array when the AI ever
  // emits an out-of-range score. Fixed-max mode keeps the dense allocation so
  // empty rubric rungs still render as sliver rows.
  const dist = useMemo(
    () =>
      isTally ? buildSparseDistribution(scores) : buildDistribution(scores, effectiveMax),
    [scores, effectiveMax, isTally],
  );
  const rowsOrdered = useMemo(() => dist.slice().sort((a, b) => b.score - a.score), [
    dist,
  ]);
  const peak = Math.max(1, ...rowsOrdered.map(b => b.count));

  // Anticipation loader: hold the bars behind a "Calculating scores"
  // placeholder until the first aiAssessedScore actually arrives. The chart
  // frame (title + status strip) is visible the whole time; only the row
  // area waits. `isScoring` guards the no-responses edge case so the loader
  // doesn't get stuck when nothing will ever come in. Back-nav: if scores
  // are already cached on mount, `scores.length > 0` skips the loader.
  const isAnticipating = started && scores.length === 0 && isScoring;

  return (
    <Stage>
      <Head>
        <Title>
          <T keyName="Scoreboard.title" defaultValue="Scoreboard" />
        </Title>
      </Head>

      <Center>
        <Chart>
          <StatusStrip $done={isDone}>
            <StatusRow>
              {isDone ? (
                <>
                  <StatusCheck>
                    <CheckIcon />
                  </StatusCheck>
                  <StatusText $done>
                    <T
                      keyName="Scoreboard.allScored"
                      defaultValue="all {total} responses scored"
                      params={{ total: safeTotal }}
                    />
                  </StatusText>
                </>
              ) : (
                <>
                  <StatusDot />
                  <StatusText>
                    <T
                      keyName="Scoreboard.aiScoring"
                      defaultValue="AI scoring responses"
                    />
                    {' · '}
                    <StatusNum>{received}</StatusNum>{' '}
                    <T
                      keyName="Scoreboard.ofTotal"
                      defaultValue="of {total}"
                      params={{ total: safeTotal }}
                    />
                  </StatusText>
                </>
              )}
            </StatusRow>
            <StatusBar>
              <StatusFill style={{ width: `${progress}%` }} />
            </StatusBar>
          </StatusStrip>
          {isAnticipating ? (
            <ChartLoader
              label={
                <T
                  keyName="Scoreboard.calculatingScores"
                  defaultValue="Calculating scores"
                />
              }
            />
          ) : (
            <Rows>
              {rowsOrdered.map((b, i) => {
                const rawW = peak === 0 ? 0 : (b.count / peak) * 100;
                const isZero = b.count === 0;
                // Tally mode never renders empty rows, so the sliver fallback
                // only applies to the fixed-max layout.
                const w = !isTally && isZero ? EMPTY_SLIVER_PCT : rawW;
                const isFull = b.score === effectiveMax && !isZero;
                return (
                  <ScoreRow
                    key={b.score}
                    bucket={b}
                    index={i}
                    isTally={isTally}
                    started={started}
                    isFull={isFull}
                    isZero={isZero}
                    targetPct={w}
                  />
                );
              })}
            </Rows>
          )}
        </Chart>
        {onSeeAllResponses && isDone && !isAnticipating && (
          <Actions>
            <SeeAllButton onClick={onSeeAllResponses}>
              <T keyName="Scoreboard.seeAllResponses" defaultValue="See all responses" />
            </SeeAllButton>
          </Actions>
        )}
      </Center>
    </Stage>
  );
};

export default AiFeedbackScoreboard;

const GP_ORANGE = '#F99746';
const GP_ORANGE_PRESS = '#CC661A';
const GP_CREAM = '#FFFCF5';
const GP_BEIGE_MAIN = theme.colors.landingPageBeige;
const GP_INK = '#1A1F18';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const Stage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${GP_BEIGE_MAIN};
  color: ${GP_INK};
  font-family: 'Nunito', sans-serif;
  padding: clamp(56px, 8vh, 128px) clamp(88px, 11vw, 220px) clamp(96px, 12vh, 192px);
  display: flex;
  flex-direction: column;
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY')};
`;

const Head = styled.header`
  text-align: center;
  margin-bottom: clamp(8px, 1.5vh, 24px);
`;

const Title = styled.h1`
  font-size: clamp(28px, 5vh, 64px);
  font-weight: 900;
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1;
  &::after {
    content: '';
    display: block;
    width: clamp(36px, 4vh, 64px);
    height: clamp(3px, 0.5vh, 6px);
    border-radius: 999px;
    background: ${GP_ORANGE};
    margin: clamp(8px, 1.2vh, 16px) auto 0;
  }
`;

const Center = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(16px, 2.5vh, 36px);
`;

const Chart = styled.div`
  width: min(92vw, 1600px);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vh, 14px);
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.85); }
`;

const checkIn = keyframes`
  from { transform: scale(0); opacity: 0; }
`;

const StatusStrip = styled.div<{ $done: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: clamp(8px, 1.2vh, 18px);
  width: 100%;
  margin-bottom: clamp(4px, 0.6vh, 10px);
  transition: opacity 0.5s ${EASE};
  opacity: ${({ $done }) => ($done ? 0.65 : 1)};
`;

const StatusRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: clamp(6px, 0.6vw, 12px);
  font-size: clamp(22px, 2.8vh, 40px);
  font-weight: 800;
  text-transform: lowercase;
  letter-spacing: 0.02em;
  color: ${GP_INK};
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: clamp(14px, 1.8vh, 22px);
  height: clamp(14px, 1.8vh, 22px);
  border-radius: 50%;
  background: ${GP_ORANGE};
  animation: ${pulse} 1.2s ${EASE} infinite;
  flex-shrink: 0;
`;

const StatusCheck = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: clamp(28px, 3.2vh, 44px);
  height: clamp(28px, 3.2vh, 44px);
  border-radius: 50%;
  background: ${GP_ORANGE};
  color: #fff;
  flex-shrink: 0;
  animation: ${checkIn} 0.5s ${EASE} backwards;
`;

const StatusNum = styled.span`
  font-weight: 900;
  color: ${GP_ORANGE_PRESS};
`;

const StatusText = styled.span<{ $done?: boolean }>`
  ${({ $done }) => ($done ? `color: ${GP_ORANGE_PRESS};` : 'text-transform: none;')}
`;

const StatusBar = styled.div`
  height: clamp(5px, 0.7vh, 10px);
  background: rgba(26, 31, 24, 0.1);
  border-radius: 999px;
  overflow: hidden;
`;

const StatusFill = styled.div`
  height: 100%;
  background: ${GP_ORANGE};
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Rows = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vh, 14px);
`;

// Sits inside <Chart> in the same slot that <Rows> would occupy, so the
// loader is centered exactly where the bars are about to flow.
const ChartLoader = styled(AnticipationLoader)`
  flex: 1;
  min-height: 0;
`;

const rowIn = keyframes`
  from { transform: translateX(-10px); opacity: 0; }
`;

/* container-type: size so children can use cqh/cqw to scale with the row's
   actual rendered size — which already adapts to row count via flex: 1. */
const Row = styled.div<{ $isFull: boolean; $isEmpty: boolean; $isTally: boolean }>`
  position: relative;
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: clamp(80px, 11vw, 200px) 1fr;
  align-items: center;
  gap: clamp(10px, 1.4vw, 28px);
  container-type: size;
  animation: ${rowIn} 0.4s ${EASE} backwards;
  /* V51 tally: hairline divider between rows, never on the last */
  ${({ $isTally }) =>
    $isTally &&
    `
    border-bottom: 1px solid rgba(26, 31, 24, 0.08);
    padding: clamp(4px, 0.6vh, 10px) 0;
    &:last-child { border-bottom: none; }
  `}
`;

const ScoreLabel = styled.div<{ $isFull: boolean }>`
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 2px;
  line-height: 1;
  color: ${({ $isFull }) => ($isFull ? GP_ORANGE_PRESS : GP_INK)};
  opacity: ${({ $isFull }) => ($isFull ? 1 : 0.75)};
  font-variant-numeric: tabular-nums;
`;

const ScoreNum = styled.span`
  /* cqh = % of the row's height, so as fewer rows fit they get bigger */
  font-size: clamp(22px, 55cqh, 160px);
  font-weight: 900;
  letter-spacing: -0.02em;
`;

const ScoreUnit = styled.span`
  font-size: clamp(10px, 14cqh, 28px);
  font-weight: 800;
  text-transform: lowercase;
  letter-spacing: 0.04em;
  opacity: 0.55;
  margin-left: 2px;
`;

const BarTrack = styled.div<{ $isTally: boolean }>`
  position: relative;
  height: 78cqh;
  min-height: 18px;
  border-radius: clamp(6px, 8cqh, 24px);
  background: transparent;
  /* Default (fixed-max) layout: overlay the count via absolute positioning so
     it floats just past the bar end on a transparent track. V51 tally mode
     instead lays the bar + count out in a flex row so the count naturally
     rides next to the bar's right edge. */
  ${({ $isTally }) =>
    $isTally
      ? `
    display: flex;
    align-items: center;
    overflow: visible;
  `
      : `
    overflow: visible;
  `}
`;

const BarFill = styled.div<{ $isTally: boolean; $isEmpty: boolean }>`
  width: 0%;
  background: ${GP_ORANGE};
  /* Empty (zero-count) rows render the tiny EMPTY_SLIVER_PCT sliver in a
     more transparent orange so the eye reads "nobody here" while still
     showing the row baseline. Populated rows stay at full saturation. */
  opacity: ${({ $isEmpty }) => ($isEmpty ? 0.25 : 0.95)};
  border-radius: inherit;
  transform-origin: left center;
  transition: width ${ANIMATION_DURATION_MS}ms ease-in-out, opacity 0.3s ease-in-out;
  will-change: width;
  ${({ $isTally }) =>
    $isTally
      ? `
    height: 100%;
    flex-shrink: 0;
    border-radius: clamp(5px, 6cqh, 16px);
  `
      : `
    position: absolute;
    inset: 0 auto 0 0;
  `}
`;

const BarCount = styled.span<{ $isEmpty: boolean; $isTally: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: clamp(3px, 0.8cqh, 8px);
  color: ${GP_INK};
  z-index: 2;
  opacity: ${({ $isEmpty }) => ($isEmpty ? 0.4 : 0.9)};
  ${({ $isTally }) =>
    $isTally
      ? `
    margin-left: clamp(8px, 1.2cqw, 20px);
    flex-shrink: 0;
    white-space: nowrap;
  `
      : `
    position: absolute;
    top: 50%;
    left: calc(var(--bar-w, 0%) + clamp(6px, 1cqw, 18px));
    transform: translateY(-50%);
    /* Track the animated bar fill. --bar-w is updated in the same React
       commit that updates BarFill's width, so the resolved left re-evaluates
       to a new value and this transition slides the label alongside. */
    transition: left ${ANIMATION_DURATION_MS}ms ease-in-out;
  `}
`;

const BarCountNum = styled.span`
  font-size: clamp(16px, 32cqh, 88px);
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1;
`;

const BarCountIcon = styled.span`
  display: inline-flex;
  align-items: center;
  svg {
    width: clamp(10px, 16cqh, 40px);
    height: clamp(10px, 16cqh, 40px);
  }
`;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const SeeAllButton = styled.button`
  background: transparent;
  border: 2px solid ${GP_INK};
  color: ${GP_INK};
  font-family: 'Nunito', sans-serif;
  font-size: clamp(14px, 2vh, 22px);
  font-weight: 900;
  border-radius: 999px;
  padding: clamp(8px, 1.4vh, 14px) clamp(20px, 2.5vw, 36px);
  cursor: pointer;
  transition: all 0.18s ${EASE};

  &:hover {
    background: ${GP_INK};
    color: ${GP_CREAM};
    transform: translateY(-1px);
  }
  &:active {
    background: ${GP_ORANGE_PRESS};
    border-color: ${GP_ORANGE_PRESS};
  }
`;
